/* eslint-disable no-console */
import { useEffect } from 'react';
import { Hub } from 'aws-amplify/utils';
import type { LoggedInUserInfo } from '../types/auth/auth.types';

interface AuthEventData {
  payload: {
    event: string;
    data?: unknown;
  };
}

interface UseAuthEventsProps {
  checkAuthState: () => Promise<void>;
  user: LoggedInUserInfo | null;
  isAuthInitialized: boolean;
  setUser: (user: LoggedInUserInfo | null) => void;
  setError: (error: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  setIsAuthInitialized: (initialized: boolean) => void;
  setSessionExpired: (expired: boolean) => void;
}

export const useAuthEvents = ({
  checkAuthState,
  user,
  isAuthInitialized,
  setUser,
  setError,
  setIsLoading,
  setIsAuthInitialized,
  setSessionExpired,
}: UseAuthEventsProps) => {
  useEffect(() => {
    const handleAuthEvents = (data: AuthEventData): void => {
      const { payload } = data;

      switch (payload.event) {
        case 'signedIn': {
          if (!isAuthInitialized || !user) {
            void checkAuthState();
          }
          break;
        }
        case 'signedOut': {
          if (user?.userAlias) {
            console.log('User signed out event received');
          }
          setUser(null);
          setError(null);
          setIsLoading(false);
          setIsAuthInitialized(false);
          break;
        }
        case 'signInFailed':
        case 'tokenRefreshFailed': {
          if (user?.userAlias) {
            setSessionExpired(true);
            console.warn('Token refresh failure, session expired');
          }
          setUser(null);
          setError(null);
          setIsLoading(false);
          setIsAuthInitialized(true);
          break;
        }
        default:
          break;
      }
    };

    const unsubscribe = Hub.listen('auth', handleAuthEvents);

    if (!isAuthInitialized) {
      void checkAuthState();
    }

    return (): void => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [
    checkAuthState,
    user,
    isAuthInitialized,
    setUser,
    setError,
    setIsLoading,
    setIsAuthInitialized,
    setSessionExpired,
  ]);
};
